import { entityMeta } from './entityMeta';

export const queryKeys = {
  integrations: (identity: string) => ["integrations", identity] as const,
  deviceSignalReadings: (deviceId?: string) => ['device-signal-readings', deviceId] as const,
  userProfile: () => ['userProfile'] as const,
  stations: {
    all: entityMeta.stations.key,
    list: (filters?: unknown) => [...entityMeta.stations.key(), 'list', filters] as const,
    detail: (id: string) => [...entityMeta.stations.key(), 'detail', id] as const,
  },
  readings: (sensorIds: string[], start: string, end: string) =>
    ['readings', sensorIds, start, end] as const,
  accumulatedValue: (sensorId: string, window: string, aggregation: string) =>
    ['accumulated-value', sensorId, window, aggregation] as const,
  sensorHistory: (sensorId?: string, range?: string) =>
    ['sensor-history', sensorId, range] as const,
  sensorFormulaInputs: (virtualSensorId?: string) =>
    ['sensor-formula-inputs', virtualSensorId] as const,
  notifications: () => ['notifications'] as const,
  // Para useTableData, podríamos necesitar un enfoque más genérico o específico por tabla
  tableData: (tableName: string, params?: unknown) => ['tableData', tableName, params] as const,
  devices: entityMeta.devices.key,
  groups: entityMeta.groups.key,
  sensors: entityMeta.sensors.key,
  sensorTypes: entityMeta.sensorTypes.key,
  measurementUnits: entityMeta.measurementUnits.key,
  actuatorTypes: entityMeta.actuatorTypes.key,
  actuators: entityMeta.actuators.key,
  latestReading: (sensorId: string) => ['latestReading', sensorId] as const,
  latestSensorReadings: (sensorIds: string[]) =>
    ['latest-sensor-readings', sensorIds] as const,
  actuatorHistory: (
    actuatorId: string,
    timeRange: string,
    customStart?: string,
    customEnd?: string
  ) => ['actuator-history', actuatorId, timeRange, customStart, customEnd] as const,
  userMemberships: () => ['userMemberships'] as const,
  pendingInvitations: () => ['pendingInvitations'] as const,
  thresholdRules: () => ['threshold_rules'] as const,
  alerts: () => ['alerts'] as const,
  activeSensors: () => ['active-sensors'] as const,
  users: (searchTerm?: string) => ['users', searchTerm] as const,
  voltageReadings: (deviceId?: string, range?: string, stationId?: string | null) =>
    ['voltage-readings', deviceId, range, stationId ?? null] as const,
  latestVoltageReadings: (deviceIds: string[]) => ['latest-voltage-readings', deviceIds] as const,
  crops: entityMeta.crops.key,
  cropTypes: entityMeta.cropTypes.key,
  cameras: entityMeta.cameras.key,
  // Catalogo FISICO (sensor_models / board_models). Solo lectura; RLS SELECT
  // abierto a authenticated. Ver features/deviceCapabilities.
  sensorModels: () => ['sensor-models'] as const,
  sensorModelVariables: () => ['sensor-model-variables'] as const,
  // Ubicacion GPS por estacion (derivada del device con fix mas reciente). Solo
  // lectura; ver features/stationsMap.
  stationLocations: () => ['station-locations'] as const,
  boardModels: () => ['board-models'] as const,
  boardModelSlots: (boardId?: string) => ['board-model-slots', boardId] as const,
  // Config-shadow de un dispositivo (reported/desired) leída de la tabla devices.
  deviceConfig: (deviceId?: string) => ['device-config', deviceId] as const,
  deviceModbusJobs: (deviceId?: string) => ['device-modbus-jobs', deviceId] as const,
  // Últimas lecturas de las variables del PLC de riego MS300 de un dispositivo.
  deviceIrrigationReadings: (deviceId?: string) =>
    ['device-irrigation-readings', deviceId] as const,
  deviceDiagnostics: (deviceId?: string) => ['device-diagnostics', deviceId] as const,
  batteryLoadProfiles: (deviceId?: string) => ['battery-load-profiles', deviceId] as const,
  stationBatteryProfiles: (deviceIds: string[]) =>
    ['station-battery-profiles', [...deviceIds].sort()] as const,
  deviceLocationHistory: (deviceId?: string) =>
    ['device-location-history', deviceId] as const,
  // Módulo Clima (rollups diarios + estadísticas de lluvia, migración 00026).
  rainStats: (sensorId?: string, stationId?: string) =>
    ['rain-stats', stationId, sensorId] as const,
  dailyRollups: (sensorIds: string[], stationId: string | undefined, fromDay: string, toDay: string) =>
    ['daily-rollups', stationId, sensorIds, fromDay, toDay] as const,
  // Resumen IA por estación (edge function station-digest).
  stationDigest: (stationId?: string, period?: string) =>
    ['station-digest', stationId, period] as const,
  // Ajustes del resumen IA por estación (tabla station_ai_settings).
  stationAiSettings: (stationId?: string) =>
    ['station-ai-settings', stationId] as const,
  // Riesgos agronómicos (migraciones 20260819): semáforos de helada/hongos.
  stationRisks: (stationIds: string[], fromDay: string) =>
    ['station-risks', [...stationIds].sort(), fromDay] as const,
  // Pronóstico persistido (weather_forecast_current), por estación.
  stationForecast: (stationId?: string, granularity?: string) =>
    ['station-forecast', stationId, granularity] as const,
  // Pronóstico instantáneo de Open-Meteo por coordenadas redondeadas.
  openMeteoSnapshot: (lat: number | null, lon: number | null) =>
    ['open-meteo-snapshot', lat, lon] as const,
  // Nombre del lugar (geocodificación inversa) por coordenadas redondeadas.
  reverseGeocode: (lat: number | null, lng: number | null) =>
    ['reverse-geocode', lat, lng] as const,
  // Fenología por cultivo (RPC get_station_phenology + tabla crop_cycles).
  stationPhenology: (stationId?: string) =>
    ['station-phenology', stationId] as const,
  cropCycle: (stationId?: string) =>
    ['crop-cycle', stationId] as const,
  // Agregados agronómicos diarios por estación (tabla station_daily_agro).
  stationDailyAgro: (stationId: string | undefined, fromDay: string, toDay: string) =>
    ['station-daily-agro', stationId, fromDay, toDay] as const,
  // Preferencias de notificación/resumen del propio perfil.
  notificationPrefs: (userId?: string) =>
    ['notification-prefs', userId] as const,
  // Estado de toda la red en una llamada (RPC get_network_overview): ubicación,
  // última lectura, pronóstico calibrado y contexto climático.
  networkOverview: () => ['network-overview'] as const,
  // Latido visual del relleno temporal; separado de devices.last_seen_at.
  activeEstimationStatus: () => ['active-estimation-status'] as const,
  // Fiabilidad medida del pronóstico por estación (RPC get_forecast_reliability).
  forecastReliability: (stationId?: string, days?: number) =>
    ['forecast-reliability', stationId, days] as const,
};
